// [src/pages/Ipt/components/IptDetail.jsx]

import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "config/apiConfig";
import { getTokenStatus } from "utils/tokenManager";
import { useBranch } from 'contexts/BranchContext';
import { useAuthSession } from "hooks/useAuthSession";

// Component imports
import TopNavigator from "components/layout/TopNavigator/TopNavigator";
import usePreventInputScroll from "hooks/usePreventInputScroll";
import Footer from "components/layout/Footer/Footer";
import BottomSheet from 'components/ui/BottomSheet/BottomSheet';
import ConfirmModal from "components/ui/Modals/ConfirmModal/ConfirmModal";
import usePreventHorizontalScroll from "hooks/usePreventHorizontalScroll";

// CSS imports
import 'styles/Font.css';

// Image imports
import starIcon from 'assets/images/icon/star-icon.svg';
import dotIcon from 'assets/images/icon/dot-icon.svg';
import defaultCoachImg from 'assets/images/coachSample/coach_ex1.svg';

/*========================================================================*/
const ReviewCard = ({ review, isExpanded, onToggle }) => {
    const contentText = review.cont || '';
    const isLongText = contentText.length > 60;

    const handleToggle = (e) => {
        e.stopPropagation();
        onToggle();
    };

    return (
        <div style={staticStyles.reviewCard}>
            <div style={staticStyles.reviewHeader}>
                <div style={staticStyles.reviewUser}>
                    <span style={staticStyles.reviewName}>{review.name}</span>
                    <span style={staticStyles.reviewDate}>{review.date}</span>
                </div>
                <div style={staticStyles.reviewRating}>
                    <img src={starIcon} alt="별" style={{ width: '12px', height: '12px' }} />
                    <span>{review.rating}</span>
                </div>
            </div>

            <div style={{
                ...staticStyles.reviewContent,
                ...(!isExpanded ? {
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: '1.6',
                } : {
                    lineHeight: '1.6'
                })
            }}>
                {contentText}
            </div>

            {isLongText && (
                <div
                    onClick={handleToggle}
                    style={{
                        marginTop: '4px',
                        display: 'flex',
                        justifyContent: isExpanded ? 'flex-end' : 'flex-start'
                    }}
                >
                    <span style={{
                        color: '#9A9A9A',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontFamily: 'Pretendard',
                        borderBottom: '1px solid transparent',
                    }}>
                        {isExpanded ? '접기' : '... 더보기'}
                    </span>
                </div>
            )}
        </div>
    );
};

/*========================================================================*/

function IptDetail() {
    usePreventInputScroll();
    usePreventHorizontalScroll();

    const navigate = useNavigate();
    const location = useLocation();
    const { currentBranch, selectBranch } = useBranch();
    const { userInfo } = useAuthSession({ requireAuth: true });

    const contextBrKey = currentBranch?.brKey;
    const { mnKey, brKey: stateBrKey, stKind: stateStKind, ctKey, exercise, date: stateDate } = location.state || {};
    const brKey = contextBrKey || stateBrKey || '1';
    const stKind = stateStKind || 'G';

    const [coach, setCoach] = useState({
        name: exercise?.exerciseName || '',
        intro: exercise?.intro || '',
        img: exercise?.img || defaultCoachImg,
        rating: '0.0',
        reviews: 0,
        detail: exercise?.intro || ''
    });

    const [activeTab, setActiveTab] = useState('INFO');
    const [currentSelectedDate, setCurrentSelectedDate] = useState(stateDate || "");
    const [reviewList, setReviewList] = useState([]);
    const [isReviewLoading, setIsReviewLoading] = useState(false);

    const [showSheet, setShowSheet] = useState(false);
    const [sheetTimes, setSheetTimes] = useState([]);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [pendingReservation, setPendingReservation] = useState(null);
    const headerRef = useRef(null);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

    const [slickStatus, setSlickStatus] = useState('N');
    const [alertModal, setAlertModal] = useState({
        isOpen: false,
        title: '',
        message: ''
    });

    const [expandedReviewKey, setExpandedReviewKey] = useState(null);

    const handleReviewToggle = (key) => {
        setExpandedReviewKey(prevKey => prevKey === key ? null : key);
    };

    useEffect(() => {
        const fetchUserStatus = async () => {
            const { token } = getTokenStatus();
            if (!token) return;

            try {
                const response = await fetch(`${API_BASE_URL}/home`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Authorization': `Bearer ${token}`
                    },
                    body: new URLSearchParams({ brKey: brKey })
                });

                if (response.ok) {
                    const data = await response.json();
                    const resData = data.data || data;
                    if (resData.slickStatus) {
                        setSlickStatus(resData.slickStatus);
                    }

                    // 전역 지점 정보가 서버 정보와 다르거나 비어있다면 업데이트
                    if (resData.brKey && (resData.brKey !== currentBranch?.brKey || resData.brName !== currentBranch?.brName)) {
                        selectBranch({
                            brKey: resData.brKey,
                            brName: resData.brName || '',
                            isProductAvailable: resData.brKey === '2' ? 'Y' : (resData.isProductAvailable || 'N'),
                            availableStKinds: resData.stKind ? resData.stKind.split(',').map(s => s.trim()) : ['E'],
                            footerInfo: resData.footerInfo ? {
                                companyName: resData.footerInfo.companyName || '',
                                ceoName: resData.footerInfo.ceoName || '',
                                businessNumber: resData.footerInfo.businessNumber || '',
                                ecommerceNumber: resData.footerInfo.ecommerceNumber || '',
                                address: resData.footerInfo.address || '',
                                csPhone: resData.footerInfo.csPhone || '',
                                email: resData.footerInfo.email || ''
                            } : {
                                companyName: resData.brCompanyName || '',
                                ceoName: resData.brCeoName || '',
                                businessNumber: resData.brBizNo || '',
                                ecommerceNumber: resData.brMailOrderNo || '',
                                address: resData.brAddr || '',
                                csPhone: resData.brTel || '',
                                email: resData.brEmail || ''
                            }
                        });
                    }
                }
            } catch (e) {
                console.error("Failed to fetch user status:", e);
            }
        };

        if (userInfo) {
            fetchUserStatus();
        }
    }, [userInfo, brKey, currentBranch, selectBranch]);

    useEffect(() => {
        const { token } = getTokenStatus();
        const fetchDetail = async () => {
            if (!ctKey) return;

            try {
                const queryParams = new URLSearchParams({ ctKey }).toString();
                const response = await fetch(`${API_BASE_URL}/weekScheduleDetail?${queryParams}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) throw new Error(`API Error: ${response.status}`);

                const data = await response.json();
                const resData = data.data || data;
                const info = resData.info || resData.ctInfo;
                if (info) {
                    setCoach({
                        name: info.name || info.exerciseName || exercise?.exerciseName || '수업 이름',
                        intro: info.cont || info.intro || exercise?.intro || '',
                        img: info.img || exercise?.img || defaultCoachImg,
                        rating: (info.rating && !isNaN(Number(info.rating))) ? Number(info.rating).toFixed(1) : '0.0',
                        reviews: info.reviews || 0,
                        detail: info.cont || info.description || info.intro || exercise?.intro || ''
                    });
                }
            } catch (e) {
                console.error("Failed to fetch detail:", e);
            }
        };

        fetchDetail();
    }, [ctKey]);

    const fetchReviews = useCallback(async () => {
        const REQUEST_API_URL = `${API_BASE_URL}/review?brKey=${brKey}&ctKey=${ctKey}`;
        const { token } = getTokenStatus();

        if (!ctKey) return;
        setIsReviewLoading(true);
        try {
            const response = await fetch(REQUEST_API_URL, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`
                }
            });

            const resData = await response.json();
            const data = resData.data || resData;

            if (data && data.list) {
                setReviewList(data.list);
            } else {
                setReviewList([]);
            }
        } catch (e) {
            console.error("Review Fetch Error:", e);
        } finally {
            setIsReviewLoading(false);
        }
    }, [brKey, ctKey]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === 'REVIEW' && reviewList.length === 0) {
            fetchReviews();
        }
    };

    const handleWriteReviewClick = () => {
        navigate('/review/write', {
            state: {
                ctKey: ctKey,
                brKey: brKey
            }
        });
    };

    const getFormattedDate = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
        return `${date.getMonth() + 1}월 ${date.getDate()}일 (${dayNames[date.getDay()]})`;
    };

    const handleDateChange = useCallback(async (date) => {
        setCurrentSelectedDate(date);
        const { token } = getTokenStatus();
        if (!token || !ctKey) return;

        try {
            const SCHEDULE_API_URL = `${API_BASE_URL}/schedule?brKey=${brKey}&ctKey=${ctKey}&stKind=${stKind}&date=${date}`;
            const response = await fetch(SCHEDULE_API_URL, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const resData = await response.json();
            const data = resData.data || resData;

            let times = [];
            if (data.list && Array.isArray(data.list)) {
                times = data.list;
            } else if (data.mnInfo && data.mnInfo.times) {
                times = data.mnInfo.times;
            } else if (data.times) {
                times = data.times;
            }
            setSheetTimes(times);

        } catch (e) {
            console.error("Schedule Fetch Error:", e);
            setSheetTimes([]);
        }
    }, [ctKey, stKind, brKey]);

    const handleReservationClick = useCallback(async () => {
        if (slickStatus !== 'Y') {
            setAlertModal({
                isOpen: true,
                title: '예약 불가',
                message: `[${currentBranch?.brName}] 수업 예약 권한이 없습니다.\n(이용권을 구매해주세요)`
            });
            return;
        }

        let targetDate = currentSelectedDate;
        if (!targetDate) {
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            targetDate = `${year}-${month}-${day}`;
        }

        await handleDateChange(targetDate);
        setShowSheet(true);
    }, [handleDateChange, currentSelectedDate, slickStatus]);

    const handleCloseSheet = () => setShowSheet(false);

    const handleSheetConfirm = useCallback((data) => {
        setPendingReservation(data);
        setIsConfirmModalOpen(true);
    }, []);

    const handleFinalReservation = useCallback(async () => {
        if (!pendingReservation) return;

        const { stKey } = pendingReservation;
        const { token } = getTokenStatus();

        const payload = {
            brKey: brKey,
            stKey: String(stKey),
            stKind: stKind
        };

        try {
            const response = await fetch(`${API_BASE_URL}/regSchedule`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`
                },
                body: new URLSearchParams(payload)
            });

            const resData = await response.json();
            const code = resData.data?.code || resData.code;
            const isSuccess = resData.status === 'success' || code === 200 || code === '200' || code === '0000';

            if (isSuccess) {
                setIsSuccessModalOpen(true);
            } else {
                const errorMsg = resData.data?.message || resData.message || '오류가 발생했습니다.';
                // alert(`예약 실패: ${errorMsg}`);
                alert(errorMsg);
            }

        } catch (e) {
            console.error("Reservation Error:", e);
            alert("예약 요청 중 오류가 발생했습니다.");
        }

        setIsConfirmModalOpen(false);
        setPendingReservation(null);
    }, [pendingReservation, stKind, brKey]);

    const handleCloseConfirmModal = () => {
        setIsConfirmModalOpen(false);
        setPendingReservation(null);
    };

    const handleCloseSuccessModal = () => {
        setIsSuccessModalOpen(false);
    };

    const closeAlertModal = () => {
        setAlertModal(prev => ({ ...prev, isOpen: false }));
    };

    const coachImages = useMemo(() => {
        const imgData = coach.img;
        if (Array.isArray(imgData) && imgData.length > 0) return imgData;
        if (typeof imgData === 'string' && imgData.trim() !== '') return [imgData];
        return [defaultCoachImg];
    }, [coach.img]);

    const dynamicMainContentStyle = useMemo(() => ({
        ...staticStyles.mainContent,
        paddingTop: '0px',
        paddingBottom: '100px',
        paddingLeft: '0',
        paddingRight: '0',
    }), []);

    return (
        <div className="backgroundWrapper" style={staticStyles.backgroundWrapper}>
            <div style={staticStyles.fixedHeaderWrapper} ref={headerRef}>
                <TopNavigator title="" isTransparent={true} />
            </div>

            <main style={dynamicMainContentStyle}>
                <div style={staticStyles.imageContainer}>
                    <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; } .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }`}</style>
                    <div className="hide-scrollbar" style={staticStyles.imageScrollContainer}>
                        {coachImages.map((imgSrc, idx) => (
                            <img key={idx} src={imgSrc} alt={`코치 ${idx}`} style={staticStyles.coachImage} />
                        ))}
                    </div>
                    <div style={staticStyles.imageOverlay} />
                    <div style={staticStyles.pageIndicator}>
                        <span style={staticStyles.pageIndicatorText}>옆으로 넘겨 더보기 👉</span>
                    </div>
                </div>

                <div style={staticStyles.nameContainer}>
                    <div style={staticStyles.nameRow}><span style={staticStyles.coachName}>{coach.name}</span></div>
                    {currentSelectedDate && (
                        <div style={staticStyles.dateRow}>
                            <span style={staticStyles.dateText}>{getFormattedDate(currentSelectedDate)}</span>
                        </div>
                    )}
                    <div style={staticStyles.reviewRow}>
                        <img src={starIcon} alt="별" style={staticStyles.starIcon} /><span
                            style={staticStyles.ratingText}>{coach.rating}</span>
                        <img src={dotIcon} alt="." style={staticStyles.dotIcon} /><span
                            style={staticStyles.reviewText}>리뷰 {coach.reviews}개</span>
                    </div>
                </div>

                <div style={staticStyles.tabContainer}>
                    <button
                        style={activeTab === 'INFO' ? staticStyles.tabButtonActive : staticStyles.tabButtonInactive}
                        onClick={() => handleTabChange('INFO')}
                    >
                        정보
                    </button>
                    <button
                        style={activeTab === 'REVIEW' ? staticStyles.tabButtonActive : staticStyles.tabButtonInactive}
                        onClick={() => handleTabChange('REVIEW')}
                    >
                        리뷰
                    </button>
                </div>

                {activeTab === 'INFO' ? (
                    <div style={staticStyles.infoContainer}>
                        {coach.detail && <p style={staticStyles.detailText}>{coach.detail}</p>}
                    </div>
                ) : (
                    <>
                        <div style={staticStyles.divider} />
                        <div style={staticStyles.reviewListContainer}>
                            {isReviewLoading ? (
                                <div style={staticStyles.emptyState}>로딩 중...</div>
                            ) : reviewList.length > 0 ? (
                                reviewList.map((review, index) => {
                                    const uniqueKey = review.key || index;
                                    return (
                                        <ReviewCard
                                            key={uniqueKey}
                                            review={review}
                                            isExpanded={expandedReviewKey === uniqueKey}
                                            onToggle={() => handleReviewToggle(uniqueKey)}
                                        />
                                    );
                                })
                            ) : (
                                <div style={staticStyles.emptyState}>등록된 리뷰가 없습니다.</div>
                            )}
                        </div>
                    </>
                )}
            </main>

            <div style={staticStyles.bottomButtonWrapper}>
                <button
                    style={staticStyles.reservationButton}
                    onClick={activeTab === 'INFO' ? handleReservationClick : handleWriteReviewClick}
                >
                    {activeTab === 'INFO' ? '예약하기' : '리뷰작성'}
                </button>
            </div>

            <BottomSheet
                show={showSheet}
                onClose={handleCloseSheet}
                onConfirm={handleSheetConfirm}
                initialData={{ coachName: coach.name }}
                availableTimes={sheetTimes}
                selectedDate={currentSelectedDate}
                onDateChange={handleDateChange}
                brName={currentBranch?.brName}
            />

            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={handleCloseConfirmModal}
                onConfirm={handleFinalReservation}
                title="예약 확인"
                message={pendingReservation ? `${pendingReservation.coachName}\n${pendingReservation.date} ${pendingReservation.time}\n\n선택하신 일시로 예약하시겠습니까?` : ""}
            />

            <ConfirmModal
                isOpen={isSuccessModalOpen}
                onClose={handleCloseSuccessModal}
                onConfirm={handleCloseSuccessModal}
                title="예약 완료"
                message="예약이 확정되었습니다."
            />

            <ConfirmModal
                isOpen={alertModal.isOpen}
                onClose={closeAlertModal}
                onConfirm={closeAlertModal}
                title={alertModal.title}
                message={alertModal.message}
                showCancel={false}
            />
        </div>
    );
}

const staticStyles = {
    backgroundWrapper: {
        background: '#0D042D', width: '100%', height: '100dvh', display: 'flex', flexDirection: 'column',
        alignItems: 'stretch', margin: '0 auto', boxSizing: 'border-box', overflowX: 'hidden',
    },
    fixedHeaderWrapper: { position: 'fixed', top: 0, left: 0, width: '100%', backgroundColor: 'transparent', zIndex: 100 },
    mainContent: {
        flex: 1, width: '100%', minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column',
        scrollbarWidth: 'none', msOverflowStyle: 'none'
    },
    imageContainer: { width: '100%', aspectRatio: '1/1', position: 'relative', flexShrink: 0, marginTop: '0', overflow: 'hidden' },
    imageScrollContainer: { display: 'flex', width: '100%', height: '100%', overflowX: 'auto', scrollSnapType: 'x mandatory', scrollBehavior: 'smooth' },
    coachImage: { minWidth: '100%', height: '100%', objectFit: 'cover', scrollSnapAlign: 'start' },
    imageOverlay: {
        position: 'absolute', bottom: 0, left: 0, width: '100%', height: '50%',
        background: 'linear-gradient(to top, #0D042D 0%, transparent 100%)', pointerEvents: 'none'
    },
    pageIndicator: {
        position: 'absolute', bottom: '20px', right: '20px', padding: '4px 8px', borderRadius: '12px',
        backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 2
    },
    pageIndicatorText: { color: '#FFFFFF', fontSize: '12px', fontFamily: 'Pretendard' },
    nameContainer: { padding: '24px', paddingBottom: '1px', display: 'flex', flexDirection: 'column', gap: '4px' },
    dateRow: { marginTop: '2px', marginBottom: '4px' },
    dateText: { fontFamily: 'Pretendard', fontWeight: 600, fontSize: '15px', color: '#8052FF' },
    tabContainer: { display: 'flex', width: '100%', borderBottom: '1px solid #2C2A40', backgroundColor: '#0D042D', zIndex: 10 },
    tabButtonActive: {
        flex: 1, height: '48px', background: 'transparent', border: 'none', borderBottom: '2px solid #8052FF',
        color: '#FFFFFF', fontFamily: 'Pretendard', fontWeight: '700', fontSize: '16px', cursor: 'pointer'
    },
    tabButtonInactive: {
        flex: 1, height: '48px', background: 'transparent', border: 'none', borderBottom: '2px solid transparent',
        color: '#747474', fontFamily: 'Pretendard', fontWeight: '500', fontSize: '16px', cursor: 'pointer'
    },
    infoContainer: { padding: '24px', paddingTop: '1px', display: 'flex', flexDirection: 'column', gap: '8px' },
    nameRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    coachName: { fontFamily: 'Pretendard', fontWeight: 700, fontSize: '24px', color: '#FCFCFA' },
    reviewRow: { display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' },
    reviewText: { fontFamily: 'Pretendard', fontWeight: 500, fontSize: '14px', color: '#9A9A9A' },
    ratingText: { fontFamily: 'Pretendard', fontWeight: 600, fontSize: '14px', color: '#FCFCFA' },
    starIcon: { width: '16px', height: '16px' },
    dotIcon: { width: '4px', height: '4px' },
    divider: { width: '100%', height: '0px', backgroundColor: '#161622' },
    detailText: { fontFamily: 'Pretendard', fontWeight: 400, fontSize: '15px', color: '#B0B0B0', lineHeight: '1.6', whiteSpace: 'pre-line' },
    bottomButtonWrapper: {
        padding: '24px 24px calc(env(safe-area-inset-bottom, 24px) + 24px) 24px', position: 'fixed',
        bottom: 0, left: 0, width: '100%', backgroundColor: '#0D042D', zIndex: 100, boxSizing: 'border-box',
        display: 'flex', justifyContent: 'center'
    },
    reservationButton: {
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)', width: '100%', height: '56px',
        borderRadius: '12px', border: 'none', color: '#FFFFFF', fontFamily: 'Pretendard', fontWeight: 700,
        fontSize: '16px', cursor: 'pointer'
    },
    reviewListContainer: { padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', boxSizing: 'border-box' },
    reviewCard: { backgroundColor: '#1B1931', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' },
    reviewHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    reviewUser: { display: 'flex', alignItems: 'center', gap: '8px' },
    reviewName: { color: '#FCFCFA', fontWeight: '600', fontSize: '14px' },
    reviewDate: { color: '#747474', fontSize: '12px' },
    reviewRating: { display: 'flex', alignItems: 'center', gap: '4px', color: '#FCFCFA', fontSize: '14px', fontWeight: '600' },
    reviewContent: { color: '#B0B0B0', fontSize: '14px', lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-all', overflowWrap: 'anywhere' },
    emptyState: { color: '#747474', textAlign: 'center', padding: '40px 0' }
};

export default IptDetail;
